// Conversational AI rule builder for the Automation tool — multi-turn chat that
// drafts rules from whitelisted triggers (built-in + custom events) and actions.
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Send, RotateCcw, CheckCircle2, ArrowRight } from "lucide-react";

const newSession = () => `rb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const actionText = (r) => {
  if (r.action_type === "email_staff") return `email ${(r.email_to || []).join(", ")} — "${r.email_subject || r.action_title}"`;
  if (r.action_type === "send_webex") return `Webex message "${r.action_title}"`;
  if (r.action_type === "notify_user") return `push notification "${r.action_title}" → ${r.assign_to_name || "acting user"}`;
  if (r.action_type === "set_deal_stage") return `move deal to ${r.target_stage}`;
  return `${r.action_type.replace("create_", "")} "${r.action_title}" in ${r.action_offset_days}d${r.assign_to_name ? ` → ${r.assign_to_name}` : ""}`;
};

export const AutomationAiChat = ({ open, onOpenChange, eventLabel, eventWorkflow, onSaved }) => {
  const [sessionId, setSessionId] = useState(newSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setThinking(true);
    try {
      const { data } = await api.post("/automations/ai-chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "ai", text: data.reply, rules: data.rules, unsupported: data.unsupported }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: e?.response?.data?.detail || "Something went wrong — try again." }]);
    } finally {
      setThinking(false);
    }
  };

  const saveRules = async (rules, msgIndex) => {
    setSaving(true);
    try {
      for (const r of rules) {
        const { assign_to_name, ...payload } = r;
        await api.post("/automations", { ...payload, workflow: eventWorkflow(r.trigger_event) });
      }
      toast.success(`${rules.length} rule${rules.length === 1 ? "" : "s"} saved and active`);
      setMessages((m) => m.map((msg, i) => (i === msgIndex ? { ...msg, saved: true } : msg)));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl p-0 gap-0" data-testid="rule-ai-chat-dialog">
        <DialogHeader className="px-4 py-3 border-b border-slate-100">
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Sparkles size={15} className="text-violet-600" /> AI rule builder
            <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-slate-500 ml-auto mr-6"
              onClick={() => { setSessionId(newSession()); setMessages([]); }} data-testid="rule-chat-reset-btn">
              <RotateCcw size={11} className="mr-1" /> New conversation
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="h-[380px] overflow-y-auto p-4 space-y-3" data-testid="rule-chat-messages">
          {messages.length === 0 && (
            <div className="text-xs text-slate-400 space-y-2 pt-4">
              <p className="font-medium text-slate-500">Describe the automation you want — I'll ask what I need, then draft the rule for your review. Try:</p>
              <p>“When a deal is won, email Allan and create a materials task 2 days later.”</p>
              <p>“When my custom stale-quote event fires, create a high-priority call for the quote owner.”</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              <div className={`max-w-[88%] rounded-sm px-3 py-2 text-xs whitespace-pre-wrap ${
                m.role === "user" ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-800"}`}>
                {m.text}
                {m.unsupported?.length > 0 && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2">
                    {m.unsupported.map((u, j) => <div key={j}>⚠ {u}</div>)}
                  </div>
                )}
                {m.rules?.length > 0 && (
                  <Card className="rounded-sm p-3 mt-2 border border-emerald-300 bg-emerald-50/50" data-testid="rule-draft-card">
                    {m.rules.map((r, j) => (
                      <div key={j} className={j > 0 ? "mt-2 pt-2 border-t border-emerald-200" : ""}>
                        <div className="text-xs font-semibold text-slate-900">{r.name}</div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1 text-[11px] text-slate-700">
                          <Badge variant="outline" className="rounded-sm font-normal text-[9px] bg-white border border-slate-300 text-slate-600">WHEN</Badge>
                          {eventLabel(r.trigger_event)}{r.trigger_stage ? ` → ${r.trigger_stage}` : ""}
                          <ArrowRight size={10} className="text-slate-400" />
                          <Badge variant="outline" className="rounded-sm font-normal text-[9px] bg-white border border-slate-300 text-slate-600">THEN</Badge>
                          {actionText(r)}
                        </div>
                      </div>
                    ))}
                    {!m.saved ? (
                      <Button size="sm" onClick={() => saveRules(m.rules, i)} disabled={saving}
                        className="rounded-sm h-7 mt-2 bg-emerald-700 hover:bg-emerald-800 text-[11px]"
                        data-testid="save-rules-btn">
                        {saving ? <Loader2 size={11} className="animate-spin mr-1" /> : <CheckCircle2 size={11} className="mr-1" />}
                        Save {m.rules.length > 1 ? `all ${m.rules.length} rules` : "rule"} & activate
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 mt-2" data-testid="rules-saved-note">
                        <CheckCircle2 size={11} /> Saved & active
                      </div>
                    )}
                  </Card>
                )}
              </div>
            </div>
          ))}
          {thinking && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> thinking…</div>}
          <div ref={bottomRef} />
        </div>
        <div className="border-t border-slate-100 p-3 flex items-end gap-2">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Describe the automation you want…" rows={2}
            className="rounded-sm text-xs resize-none" data-testid="rule-chat-input" />
          <Button onClick={send} disabled={thinking || !input.trim()}
            className="rounded-sm h-9 bg-violet-700 hover:bg-violet-800" data-testid="rule-chat-send-btn">
            <Send size={13} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
